import {
  CommentEntityType,
  GetQuestionCommentsAndVerdictByEntityAndTaskIdDocument,
  GetQuestionReviewTaskByIdDocument,
  GetQuestionReviewTaskOwnerAssigneeAndTaskByIdDocument,
  useCreateOrUpdateQuestionVerdictAndCreateCommentMutation,
  useCreateOrUpdateQuestionVerdictMutation,
  useUpdateTaskStatusByIdMutation,
  VerdictEntityType,
  WorkflowStepCode,
} from '@exam-management/graphql';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver, type PropsWithChildren } from '@shared/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { mappedVerdict } from '../../constants';
import { useParseContentForQuestionReview } from '../../hooks';
import { useQuestionReviewTaskQuestionDetails } from '../question-review-task-question-details-provider';

import {
  type QuestionReviewResultVerdict,
  QuestionReviewTaskQuestionDetailsFormSchema,
  type QuestionReviewTaskQuestionDetailsFormValues,
} from './schema';

interface QuestionReviewTaskQuestionDetailsFormProviderProps extends PropsWithChildren {
  initialValues: Partial<QuestionReviewTaskQuestionDetailsFormValues>;
}

export default function QuestionReviewTaskQuestionDetailsFormProvider(
  props: Readonly<QuestionReviewTaskQuestionDetailsFormProviderProps>,
) {
  const { initialValues, children } = props;

  const { mainTaskDetails, question, comment, verdict } = useQuestionReviewTaskQuestionDetails();

  const { parseContentForDisplay } = useParseContentForQuestionReview({
    taskId: mainTaskDetails.id,
    questionId: question.id,
  });

  const { showSnackbar } = useSnackbar();

  const showSubmitSuccessfulSnackbar = () => {
    showSnackbar({
      message: 'Review Result has been saved',
      variant: 'success',
    });
  };

  const showSubmitErrorSnackbar = useCallback(() => {
    showSnackbar({
      message: 'Unable to Save Review Result',
      variant: 'error',
    });
  }, [showSnackbar]);

  const formContext = useForm({
    resolver: zodResolver(QuestionReviewTaskQuestionDetailsFormSchema),
  });

  const handleResetForm = useCallback(async () => {
    const parsedDescription = await parseContentForDisplay(initialValues.description ?? '');

    formContext.reset({
      result: initialValues.result,
      description: parsedDescription,
    });
  }, [initialValues, parseContentForDisplay, formContext]);

  useEffect(() => {
    handleResetForm();
  }, [handleResetForm]);

  const [updateTaskStatus] = useUpdateTaskStatusByIdMutation({
    onCompleted: () => {
      showSubmitSuccessfulSnackbar();
    },
    onError: () => {
      showSubmitErrorSnackbar();
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQuestionReviewTaskOwnerAssigneeAndTaskByIdDocument,
        variables: {
          input: {
            taskId: mainTaskDetails.id,
          },
        },
      },
      {
        query: GetQuestionReviewTaskByIdDocument,
        variables: {
          input: {
            taskId: mainTaskDetails.id,
          },
        },
      },
    ],
  });

  const mutationOptions = useMemo(
    () => ({
      onCompleted: () => {
        formContext.reset({
          result: verdict?.verdict as unknown as QuestionReviewResultVerdict,
          description: comment?.content,
        });
      },
      onError: () => {
        showSubmitErrorSnackbar();
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionReviewTaskOwnerAssigneeAndTaskByIdDocument,
          variables: {
            input: {
              taskId: mainTaskDetails.id,
            },
          },
        },
        {
          query: GetQuestionCommentsAndVerdictByEntityAndTaskIdDocument,
          variables: {
            verdictInput: {
              entityId: question.id,
              entityType: VerdictEntityType.Question,
              taskId: mainTaskDetails.id,
            },
            commentInput: {
              entityId: question.id,
              entityType: CommentEntityType.Question,
              taskId: mainTaskDetails.id,
            },
          },
        },
      ],
    }),
    [
      showSubmitErrorSnackbar,
      mainTaskDetails.id,
      question.id,
      comment?.content,
      verdict?.verdict,
      formContext,
    ],
  );

  const [insertVerdict] = useCreateOrUpdateQuestionVerdictMutation(mutationOptions);

  const [insertVerdictAndComment] =
    useCreateOrUpdateQuestionVerdictAndCreateCommentMutation(mutationOptions);

  const onSubmit = async (formValues: QuestionReviewTaskQuestionDetailsFormValues) => {
    if (!formValues.description) {
      await insertVerdict({
        variables: {
          input: {
            taskId: mainTaskDetails.id,
            entityId: question.id,
            entityType: VerdictEntityType.Question,
            verdict: mappedVerdict[formValues.result],
          },
        },
      });
    } else {
      await insertVerdictAndComment({
        variables: {
          verdictInput: {
            taskId: mainTaskDetails.id,
            entityId: question.id,
            entityType: VerdictEntityType.Question,
            verdict: mappedVerdict[formValues.result],
          },
          commentInput: {
            taskId: mainTaskDetails.id,
            entityId: question.id,
            entityType: CommentEntityType.Question,
            content: parseContentForApi(formValues.description),
          },
        },
      });
    }

    if (mainTaskDetails.currentWorkflowStep.code === WorkflowStepCode.QuestionReviewTodo) {
      await updateTaskStatus({
        variables: {
          updateTaskStatusInput: {
            taskId: mainTaskDetails.id,
            workflowStepId: mainTaskDetails.currentWorkflowStep.stepTransitions[0].id,
          },
        },
      });
    } else {
      showSubmitSuccessfulSnackbar();
    }
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>{children}</Form>
    </FormProvider>
  );
}
